import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  AccountingAttachmentKind,
  AccountingAttachInvoicePayload,
  AccountingCancelExpensePayload,
  AccountingContactFollowUpPayload,
  AccountingCreateExpensePayload,
  AccountingEmailReminderSendPayload,
  AccountingListQuery,
  AccountingSupplierEmailTemplateSettings,
  AccountingManualReminderPayload,
  AccountingPayExpensePayload,
  AccountingPrintEventPayload,
  AccountingReportFormat,
  AccountingReportType,
  AccountingScopeQuery,
  AccountingSupplierLookupQuery,
  AccountingTejPatchPayload,
  AccountingUpdateExpensePayload,
  AccountingWithholdingReviewPayload,
} from '../../../models/accounting'
import {
  normalizeAccountingEmailReminderSendPayload,
  parseAccountingSupplierEmailTemplateSettings,
} from './supplierEmailTemplate'
import {
  accountingAuditUrl,
  accountingExpenseAttachmentByIdUrl,
  accountingExpenseAttachmentDownloadUrl,
  accountingExpenseAttachmentsUrl,
  accountingExpenseByIdUrl,
  accountingExpenseCancelUrl,
  accountingExpenseInvoiceUrl,
  accountingExpensePaymentsUrl,
  accountingExpenseTejUrl,
  accountingExpenseUpdateUrl,
  accountingExpenseValidateUrl,
  accountingExpenseWithholdingReviewUrl,
  accountingExpensesUrl,
  accountingInvoiceFollowUpByIdUrl,
  accountingInvoiceFollowUpContactUrl,
  accountingInvoiceFollowUpEmailReminderPrepareUrl,
  accountingInvoiceFollowUpEmailReminderSendUrl,
  accountingInvoiceFollowUpEmailReminderStatusUrl,
  accountingInvoiceFollowUpEmailTemplateUrl,
  accountingInvoiceFollowUpManualRemindersUrl,
  accountingInvoiceFollowUpRemindersUrl,
  accountingInvoiceFollowUpScheduleUrl,
  accountingInvoiceFollowUpsUrl,
  accountingSuppliersLookupUrl,
  accountingPaymentsUrl,
  accountingReportUrl,
  accountingSummaryUrl,
  accountingTejPendingUrl,
  accountingTicketByIdUrl,
  accountingTicketPrintEventsUrl,
  accountingTicketPrintHistoryUrl,
  accountingTicketsUrl,
  accountingWithholdingPendingUrl,
} from '../../api'
import {parseAccountingApiError} from './errors'
import {
  accountingFallbackExportName,
  accountingReportAcceptHeader,
  buildAccountingCreateExpenseFormData,
  buildAccountingListParams,
  buildAccountingScopeParams,
  dedupeInflightGet,
  inspectAccountingExportBlob,
  parseAccountingContentDisposition,
  withQuery,
} from './helpers'

function isCanceled(error: unknown): boolean {
  return axios.isCancel(error) || (error as {code?: string})?.code === 'ERR_CANCELED'
}

export const fetchAccountingSummary = createAsyncThunk(
  'accounting/summary',
  async (payload: AccountingScopeQuery | void, {rejectWithValue, signal}) => {
    const query = payload || {}
    const url = withQuery(accountingSummaryUrl, buildAccountingScopeParams(query))
    try {
      const res = await dedupeInflightGet(`GET ${url}`, () => axios.get(url, {signal}))
      return res.data
    } catch (error) {
      if (isCanceled(error) || signal.aborted) throw error
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingPayments = createAsyncThunk(
  'accounting/payments',
  async (payload: AccountingListQuery | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        withQuery(accountingPaymentsUrl, buildAccountingListParams(payload || {}))
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingExpenses = createAsyncThunk(
  'accounting/expenses',
  async (payload: AccountingListQuery | void, {rejectWithValue, signal}) => {
    const url = withQuery(accountingExpensesUrl, buildAccountingListParams(payload || {}))
    try {
      const res = await dedupeInflightGet(`GET ${url}`, () => axios.get(url, {signal}))
      return res.data
    } catch (error) {
      if (isCanceled(error) || signal.aborted) throw error
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingExpenseById = createAsyncThunk(
  'accounting/expenseById',
  async (payload: {id: string} & AccountingScopeQuery, {rejectWithValue}) => {
    try {
      const {id, ...query} = payload
      const res = await axios.get(
        withQuery(accountingExpenseByIdUrl(id), buildAccountingScopeParams(query))
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const createAccountingExpense = createAsyncThunk(
  'accounting/createExpense',
  async (
    payload: AccountingCreateExpensePayload & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {scope, academicYear, from, to, file, invoice, bl, ...body} = payload
      const url = withQuery(
        accountingExpensesUrl,
        buildAccountingScopeParams({
          scope,
          establishmentId: payload.establishmentId,
          academicYear,
          from,
          to,
        })
      )
      const headers: Record<string, string> = {}
      if (payload.idempotencyKey) headers['Idempotency-Key'] = payload.idempotencyKey
      const hasFiles = Boolean(file || invoice || bl)
      if (hasFiles) {
        const form = buildAccountingCreateExpenseFormData(
          {...body, invoice, bl},
          file
        )
        const res = await axios.post(url, form, {headers})
        return res.data
      }
      const res = await axios.post(url, body, {headers})
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const updateAccountingExpense = createAsyncThunk(
  'accounting/updateExpense',
  async (
    payload: {id: string; body: AccountingUpdateExpensePayload} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, body, ...query} = payload
      const res = await axios.post(
        withQuery(accountingExpenseUpdateUrl(id), buildAccountingScopeParams(query)),
        body
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const validateAccountingExpense = createAsyncThunk(
  'accounting/validateExpense',
  async (payload: {id: string} & AccountingScopeQuery, {rejectWithValue}) => {
    try {
      const {id, ...query} = payload
      const res = await axios.post(
        withQuery(accountingExpenseValidateUrl(id), buildAccountingScopeParams(query)),
        {}
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const payAccountingExpense = createAsyncThunk(
  'accounting/payExpense',
  async (
    payload: {id: string; body: AccountingPayExpensePayload} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, body, ...query} = payload
      const res = await axios.post(
        withQuery(accountingExpensePaymentsUrl(id), buildAccountingScopeParams(query)),
        body
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const cancelAccountingExpense = createAsyncThunk(
  'accounting/cancelExpense',
  async (
    payload: {id: string; body: AccountingCancelExpensePayload} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, body, ...query} = payload
      const res = await axios.post(
        withQuery(accountingExpenseCancelUrl(id), buildAccountingScopeParams(query)),
        body
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingTickets = createAsyncThunk(
  'accounting/tickets',
  async (payload: AccountingListQuery | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        withQuery(accountingTicketsUrl, buildAccountingListParams(payload || {}))
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingTicketById = createAsyncThunk(
  'accounting/ticketById',
  async (payload: {id: string} & AccountingScopeQuery, {rejectWithValue}) => {
    try {
      const {id, ...query} = payload
      const res = await axios.get(
        withQuery(accountingTicketByIdUrl(id), buildAccountingScopeParams(query))
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingTicketPrintHistory = createAsyncThunk(
  'accounting/ticketPrintHistory',
  async (payload: {id: string} & AccountingScopeQuery, {rejectWithValue}) => {
    try {
      const {id, ...query} = payload
      const res = await axios.get(
        withQuery(accountingTicketPrintHistoryUrl(id), buildAccountingScopeParams(query))
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const addAccountingTicketPrintEvent = createAsyncThunk(
  'accounting/ticketPrintEvent',
  async (
    payload: {id: string; body: AccountingPrintEventPayload} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, body, ...query} = payload
      const res = await axios.post(
        withQuery(accountingTicketPrintEventsUrl(id), buildAccountingScopeParams(query)),
        body
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingReport = createAsyncThunk(
  'accounting/report',
  async (
    payload: AccountingListQuery & {type: AccountingReportType; format?: AccountingReportFormat},
    {rejectWithValue}
  ) => {
    try {
      const {type, format = 'json', ...query} = payload
      const params = buildAccountingListParams(query)
      params.set('format', format)
      const accept = accountingReportAcceptHeader(format)
      if (format === 'json') {
        const res = await axios.get(withQuery(accountingReportUrl(type), params), {
          headers: {Accept: accept},
        })
        return res.data
      }
      const res = await axios.get(withQuery(accountingReportUrl(type), params), {
        responseType: 'blob',
        headers: {Accept: accept, 'Cache-Control': 'no-store', Pragma: 'no-cache'},
      })
      const contentType = String(res.headers['content-type'] || res.headers['Content-Type'] || '')
      const disposition = String(
        res.headers['content-disposition'] || res.headers['Content-Disposition'] || ''
      )
      if (!(res.data instanceof Blob)) {
        if (format === 'pdf') {
          return rejectWithValue({
            success: false as const,
            error: 'Accounting request failed',
            errorCode: 'PDF_RENDERER_UNAVAILABLE',
            status: res.status,
          })
        }
        return {
          success: true,
          format,
          type,
          bodyText: String(res.data || ''),
          contentType,
        }
      }
      const inspected = await inspectAccountingExportBlob(res.data, contentType)
      if (inspected.kind === 'error') {
        return rejectWithValue({
          success: false as const,
          error: inspected.error,
          errorCode: inspected.errorCode,
          status: res.status,
        })
      }
      if (format === 'pdf') {
        if (inspected.kind !== 'pdf') {
          return rejectWithValue({
            success: false as const,
            error: inspected.kind === 'text' ? inspected.text.slice(0, 180) : 'Accounting request failed',
            errorCode: 'PDF_RENDERER_UNAVAILABLE',
            status: res.status,
          })
        }
        return {
          success: true,
          format,
          type,
          blob: inspected.blob,
          fileName: parseAccountingContentDisposition(disposition, accountingFallbackExportName(type, 'pdf')),
          contentType: inspected.blob.type || contentType,
        }
      }
      const bodyText = inspected.kind === 'text' ? inspected.text : await res.data.text()
      return {
        success: true,
        format,
        type,
        bodyText,
        contentType,
      }
    } catch (error) {
      const axiosError = error as {response?: {data?: unknown; status?: number; headers?: Record<string, string>}}
      const data = axiosError.response?.data
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        const contentType = String(
          axiosError.response?.headers?.['content-type'] ||
            axiosError.response?.headers?.['Content-Type'] ||
            data.type ||
            ''
        )
        const inspected = await inspectAccountingExportBlob(data, contentType)
        if (inspected.kind === 'error') {
          return rejectWithValue({
            success: false as const,
            error: inspected.error,
            errorCode: inspected.errorCode,
            status: axiosError.response?.status,
          })
        }
      }
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingAudit = createAsyncThunk(
  'accounting/audit',
  async (payload: AccountingListQuery | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        withQuery(accountingAuditUrl, buildAccountingListParams(payload || {}))
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const attachAccountingExpenseInvoice = createAsyncThunk(
  'accounting/attachInvoice',
  async (
    payload: {id: string; body: AccountingAttachInvoicePayload} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, body, ...query} = payload
      const res = await axios.post(
        withQuery(accountingExpenseInvoiceUrl(id), buildAccountingScopeParams(query)),
        body
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const reviewAccountingExpenseWithholding = createAsyncThunk(
  'accounting/reviewWithholding',
  async (
    payload: {id: string; body: AccountingWithholdingReviewPayload} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, body, ...query} = payload
      const res = await axios.post(
        withQuery(accountingExpenseWithholdingReviewUrl(id), buildAccountingScopeParams(query)),
        body
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const updateAccountingExpenseTej = createAsyncThunk(
  'accounting/updateTej',
  async (
    payload: {id: string; body: AccountingTejPatchPayload} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, body, ...query} = payload
      const res = await axios.post(
        withQuery(accountingExpenseTejUrl(id), buildAccountingScopeParams(query)),
        body
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingInvoiceFollowUps = createAsyncThunk(
  'accounting/invoiceFollowUps',
  async (payload: AccountingListQuery | void, {rejectWithValue, signal}) => {
    const url = withQuery(accountingInvoiceFollowUpsUrl, buildAccountingListParams(payload || {}))
    try {
      const res = await axios.get(url, {signal})
      return res.data
    } catch (error) {
      if (isCanceled(error) || signal.aborted) throw error
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingInvoiceFollowUpById = createAsyncThunk(
  'accounting/invoiceFollowUpById',
  async (payload: {id: string} & AccountingScopeQuery, {rejectWithValue}) => {
    try {
      const {id, ...query} = payload
      const res = await axios.get(
        withQuery(accountingInvoiceFollowUpByIdUrl(id), buildAccountingScopeParams(query))
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const contactAccountingInvoiceFollowUp = createAsyncThunk(
  'accounting/invoiceFollowUpContact',
  async (
    payload: {id: string; body: AccountingContactFollowUpPayload} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, body, ...query} = payload
      const res = await axios.post(
        withQuery(accountingInvoiceFollowUpContactUrl(id), buildAccountingScopeParams(query)),
        body
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const scheduleAccountingInvoiceFollowUp = createAsyncThunk(
  'accounting/invoiceFollowUpSchedule',
  async (
    payload: {id: string; nextFollowUpAt: string} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, nextFollowUpAt, ...query} = payload
      const res = await axios.post(
        withQuery(accountingInvoiceFollowUpScheduleUrl(id), buildAccountingScopeParams(query)),
        {nextFollowUpAt}
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const lookupAccountingSuppliers = createAsyncThunk(
  'accounting/suppliersLookup',
  async (payload: AccountingSupplierLookupQuery & AccountingScopeQuery, {rejectWithValue, signal}) => {
    try {
      const params = buildAccountingScopeParams({
        scope: payload.scope || (payload.establishmentId ? 'ESTABLISHMENT' : undefined),
        establishmentId: payload.establishmentId,
        academicYear: payload.academicYear,
        from: payload.from,
        to: payload.to,
      })
      if (payload.supplierName) params.set('supplierName', payload.supplierName)
      const res = await axios.get(withQuery(accountingSuppliersLookupUrl, params), {signal})
      return res.data
    } catch (error) {
      if (isCanceled(error) || signal.aborted) throw error
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const prepareAccountingInvoiceFollowUpEmailReminder = createAsyncThunk(
  'accounting/invoiceFollowUpEmailReminderPrepare',
  async (payload: {id: string} & AccountingScopeQuery, {rejectWithValue}) => {
    try {
      const {id, ...query} = payload
      const res = await axios.post(
        withQuery(accountingInvoiceFollowUpEmailReminderPrepareUrl(id), buildAccountingScopeParams(query)),
        {}
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const sendAccountingInvoiceFollowUpEmailReminder = createAsyncThunk(
  'accounting/invoiceFollowUpEmailReminderSend',
  async (
    payload: {
      id: string
      body: AccountingEmailReminderSendPayload
      idempotencyKey: string
    } & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, body, idempotencyKey, ...query} = payload
      const outbound = normalizeAccountingEmailReminderSendPayload(body)
      const res = await axios.post(
        withQuery(accountingInvoiceFollowUpEmailReminderSendUrl(id), buildAccountingScopeParams(query)),
        outbound,
        {headers: {'Idempotency-Key': idempotencyKey}}
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingInvoiceFollowUpEmailReminderStatus = createAsyncThunk(
  'accounting/invoiceFollowUpEmailReminderStatus',
  async (
    payload: {id: string; idempotencyKey: string} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, idempotencyKey, ...query} = payload
      const res = await axios.get(
        withQuery(accountingInvoiceFollowUpEmailReminderStatusUrl(id), buildAccountingScopeParams(query)),
        {headers: {'Idempotency-Key': idempotencyKey}}
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingInvoiceFollowUpReminders = createAsyncThunk(
  'accounting/invoiceFollowUpReminders',
  async (payload: {id: string} & AccountingScopeQuery, {rejectWithValue, signal}) => {
    try {
      const {id, ...query} = payload
      const res = await axios.get(
        withQuery(accountingInvoiceFollowUpRemindersUrl(id), buildAccountingScopeParams(query)),
        {signal}
      )
      return res.data
    } catch (error) {
      if (isCanceled(error) || signal.aborted) throw error
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingInvoiceFollowUpEmailTemplate = createAsyncThunk(
  'accounting/invoiceFollowUpEmailTemplate',
  async (payload: AccountingScopeQuery | undefined, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        withQuery(accountingInvoiceFollowUpEmailTemplateUrl, buildAccountingScopeParams(payload || {}))
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const saveAccountingInvoiceFollowUpEmailTemplate = createAsyncThunk(
  'accounting/saveInvoiceFollowUpEmailTemplate',
  async (
    payload: {body: AccountingSupplierEmailTemplateSettings} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {body, ...query} = payload
      const res = await axios.put(
        withQuery(accountingInvoiceFollowUpEmailTemplateUrl, buildAccountingScopeParams(query)),
        parseAccountingSupplierEmailTemplateSettings(body)
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const recordAccountingInvoiceFollowUpManualReminder = createAsyncThunk(
  'accounting/invoiceFollowUpManualReminder',
  async (
    payload: {
      id: string
      body: AccountingManualReminderPayload
      idempotencyKey: string
    } & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, body, idempotencyKey, ...query} = payload
      const res = await axios.post(
        withQuery(accountingInvoiceFollowUpManualRemindersUrl(id), buildAccountingScopeParams(query)),
        body,
        {headers: {'Idempotency-Key': idempotencyKey}}
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingWithholdingPending = createAsyncThunk(
  'accounting/withholdingPending',
  async (payload: AccountingScopeQuery | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        withQuery(accountingWithholdingPendingUrl, buildAccountingScopeParams(payload || {}))
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingTejPending = createAsyncThunk(
  'accounting/tejPending',
  async (payload: AccountingListQuery | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        withQuery(accountingTejPendingUrl, buildAccountingListParams(payload || {}))
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const fetchAccountingExpenseAttachments = createAsyncThunk(
  'accounting/expenseAttachments',
  async (payload: {id: string} & AccountingScopeQuery, {rejectWithValue}) => {
    try {
      const {id, ...query} = payload
      const res = await axios.get(
        withQuery(accountingExpenseAttachmentsUrl(id), buildAccountingScopeParams(query))
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const uploadAccountingExpenseAttachment = createAsyncThunk(
  'accounting/uploadExpenseAttachment',
  async (
    payload: {
      id: string
      file: File
      kind: AccountingAttachmentKind
      onUploadProgress?: (percent: number) => void
    } & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, file, kind, onUploadProgress, ...query} = payload
      const form = new FormData()
      form.append('file', file)
      form.append('kind', kind)
      const res = await axios.post(
        withQuery(accountingExpenseAttachmentsUrl(id), buildAccountingScopeParams(query)),
        form,
        {
          onUploadProgress: (event) => {
            if (!onUploadProgress || !event.total) return
            onUploadProgress(Math.round((event.loaded / event.total) * 100))
          },
        }
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const downloadAccountingExpenseAttachment = createAsyncThunk(
  'accounting/downloadExpenseAttachment',
  async (
    payload: {id: string; attachmentId: string; fileName?: string} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, attachmentId, fileName, ...query} = payload
      const res = await axios.get(
        withQuery(
          accountingExpenseAttachmentDownloadUrl(id, attachmentId),
          buildAccountingScopeParams(query)
        ),
        {responseType: 'blob'}
      )
      const contentType = String(res.headers['content-type'] || '')
      if (contentType.includes('application/json') && res.data instanceof Blob) {
        const text = await res.data.text()
        try {
          const json = JSON.parse(text)
          return rejectWithValue({
            success: false as const,
            error: json.message?.fr || json.message?.en || json.error || 'Download failed',
            errorCode: json.errorCode,
            status: res.status,
          })
        } catch {
          return rejectWithValue({
            success: false as const,
            error: 'Download failed',
            status: res.status,
          })
        }
      }
          return {
            fileBlob: res.data,
            contentType,
            contentDisposition: res.headers['content-disposition'] as string | undefined,
            fileName,
          }
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)

export const archiveAccountingExpenseAttachment = createAsyncThunk(
  'accounting/archiveExpenseAttachment',
  async (
    payload: {id: string; attachmentId: string} & AccountingScopeQuery,
    {rejectWithValue}
  ) => {
    try {
      const {id, attachmentId, ...query} = payload
      const res = await axios.delete(
        withQuery(
          accountingExpenseAttachmentByIdUrl(id, attachmentId),
          buildAccountingScopeParams(query)
        )
      )
      return res.data
    } catch (error) {
      return rejectWithValue(parseAccountingApiError(error))
    }
  }
)
